import { Menu } from 'lucide-react';
import { type ReactNode } from 'react';

import ReleasePublishedNotesList from '@/components/manage-release-notes/release-published-notes-list';
import BurgerMenuFooter from '@/components/shared/burger-menu-footer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import useBreakpoint from '@/hooks/use-break-point';
import { cn } from '@/lib/utils';
import useReleaseNoteStore from '@/store/useReleaseNoteStore.ts';

interface BurgerMenuProps {
  children: ReactNode;
  header: ReactNode;
}

const BurgerMenu = ({ children, header }: BurgerMenuProps) => {
  const isMobile = useBreakpoint('mobile');
  const { isBurgerMenuOpen, burgerMenuView, setIsBurgerMenuOpen, setBurgerMenuView } =
    useReleaseNoteStore();

  const isMenuView = burgerMenuView === 'menu';

  const handleOpenChange = (nextOpen: boolean) => setIsBurgerMenuOpen(nextOpen);
  const openReleaseNotes = () => setBurgerMenuView('release-notes');
  const backToMenu = () => setBurgerMenuView('menu');

  const renderContent = () => {
    if (!isMenuView) {
      return <ReleasePublishedNotesList onBack={backToMenu} />;
    }

    return (
      <>
        <ScrollArea className="flex-1 w-full px-2 py-4">{children}</ScrollArea>
        <BurgerMenuFooter onOpenReleaseNotes={openReleaseNotes} />
      </>
    );
  };

  return (
    <Sheet open={isBurgerMenuOpen} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <button aria-label="open-menu">
          <Menu size={24} className="text-white cursor-pointer" strokeWidth={3} />
        </button>
      </SheetTrigger>

      <SheetContent
        side="right"
        overlayClassName="z-52"
        className={cn(
          'transition-all duration-300 ease-in-out top-0 h-full border-0 w-[344px] z-52',
          {
            'w-[288px]': isMobile,
          }
        )}
        closeIconClassName="text-white"
      >
        <SheetHeader className="text-left py-4 px-5 bg-primary">
          <SheetTitle className="text-xl text-primary-foreground font-semibold">
            {header}
          </SheetTitle>
        </SheetHeader>

        {renderContent()}
      </SheetContent>
    </Sheet>
  );
};

export default BurgerMenu;
